import { useState } from 'react';
import {
  Button,
  Card,
  Badge,
  Select,
  Textarea,
  Modal,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import {
  useReviewAccessRequest,
  useAccessRequests,
  type AccessRequest,
  type AccessRequestStatus,
} from './useIam';
import { formatDate } from '../../shared/utils/format';

/* ── Types ── */

type StatusFilter = 'all' | AccessRequestStatus;

/* ── Helpers ── */

/* O dominio tem cinco estados, em maiusculas. O mapa anterior era minusculo e
   com tres chaves: nenhuma casava, entao o selo mostrava o valor cru, o filtro
   "Pendentes" nao filtrava nada e os botoes de revisao nunca apareciam.
   CANCELLED e EXPIRED existiam e a tela nao sabia exibi-los. */
const statusVariant: Record<AccessRequestStatus, 'warning' | 'success' | 'error' | 'neutral'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
  EXPIRED: 'neutral',
};

const statusLabel: Record<AccessRequestStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  CANCELLED: 'Cancelado',
  EXPIRED: 'Expirado',
};

/* ── Component ── */

export function AccessRequestsPage(): JSX.Element {
  const { addToast } = useToast();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');
  const [reviewModal, setReviewModal] = useState<{
    request: AccessRequest;
    action: 'approve' | 'reject';
  } | null>(null);
  const [reviewJustification, setReviewJustification] = useState('');

  const { data, isLoading, error } = useAccessRequests();

  const reviewMutation = useReviewAccessRequest();

  const filtered = (data ?? []).filter((r) =>
    statusFilter === 'all' ? true : r.status === statusFilter,
  );

  const openReview = (request: AccessRequest, action: 'approve' | 'reject'): void => {
    setReviewModal({ request, action });
    setReviewJustification('');
  };

  const handleReview = (): void => {
    if (!reviewModal) return;

    reviewMutation.mutate(
      {
        id: reviewModal.request.id,
        action: reviewModal.action,
        /* `rejectionReason` so faz sentido na recusa — e e o unico campo de
           texto que `ReviewAccessRequestDto` aceita. */
        ...(reviewModal.action === 'reject' && reviewJustification
          ? { rejectionReason: reviewJustification }
          : {}),
      },
      {
        onSuccess: () => {
          addToast(
            reviewModal.action === 'approve'
              ? 'Solicitação aprovada com sucesso'
              : 'Solicitação rejeitada',
            'success',
          );
          setReviewModal(null);
          setReviewJustification('');
        },
        onError: (err) => {
          addToast(
            err instanceof Error ? err.message : 'Erro ao revisar solicitação',
            'error',
          );
        },
      },
    );
  };

  return (
    <div className="iam-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Solicitações de Acesso</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Solicitações de acesso pendentes de aprovação.</p>
        </div>
      </div>

      <Card>
        <div className="filters">
          <Select
            label="Filtrar por status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">Todas</option>
            <option value="PENDING">Pendentes</option>
            <option value="APPROVED">Aprovadas</option>
            <option value="REJECTED">Rejeitadas</option>
            <option value="CANCELLED">Canceladas</option>
            <option value="EXPIRED">Expiradas</option>
          </Select>
        </div>

        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={6} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">
              Erro ao carregar solicitações: {(error as Error).message}
            </p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="empty-container">
            <p className="empty-message">
              {statusFilter === 'PENDING'
                ? 'Nenhuma solicitação pendente.'
                : 'Nenhuma solicitação encontrada.'}
            </p>
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Papel</th>
                  <th>Justificativa</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((request) => (
                  <tr key={request.id}>
                    <td>
                      {/* `userName` e `roleName` passaram a existir no backend
                          (todo-review7); ate entao as duas colunas principais
                          desta tela vinham vazias. */}
                      {request.userName ?? <code>{request.userId}</code>}
                    </td>
                    <td>
                      <Badge variant="info">{request.roleName ?? request.roleId}</Badge>
                    </td>
                    <td className="justification-cell" title={request.reason}>
                      {request.reason.length > 60
                        ? `${request.reason.slice(0, 60)}...`
                        : request.reason}
                    </td>
                    <td>
                      <Badge variant={statusVariant[request.status] ?? 'neutral'}>
                        {statusLabel[request.status] ?? request.status}
                      </Badge>
                    </td>
                    <td>{formatDate(request.createdAt)}</td>
                    <td>
                      {request.status === 'PENDING' ? (
                        <div className="review-actions">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => openReview(request, 'approve')}
                          >
                            Aprovar
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => openReview(request, 'reject')}
                          >
                            Rejeitar
                          </Button>
                        </div>
                      ) : (
                        <span className="reviewed-info">
                          {request.rejectionReason
                            ? `Motivo: ${request.rejectionReason.length > 30 ? request.rejectionReason.slice(0, 30) + '...' : request.rejectionReason}`
                            : request.approvedAt
                              ? `Aprovado em ${formatDate(request.approvedAt)}`
                              : '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={!!reviewModal}
        onClose={() => { setReviewModal(null); setReviewJustification(''); }}
        title={
          reviewModal?.action === 'approve'
            ? 'Aprovar Solicitação'
            : 'Rejeitar Solicitação'
        }
        footer={
          <div className="modal-actions">
            <Button
              variant="ghost"
              onClick={() => { setReviewModal(null); setReviewJustification(''); }}
            >
              Cancelar
            </Button>
            <Button
              variant={reviewModal?.action === 'approve' ? 'primary' : 'danger'}
              onClick={handleReview}
              loading={reviewMutation.isPending}
              disabled={reviewMutation.isPending}
            >
              {reviewModal?.action === 'approve' ? 'Aprovar' : 'Rejeitar'}
            </Button>
          </div>
        }
      >
        <div className="modal-form">
          {reviewModal && (
            <>
              <p className="review-summary">
                <strong>Usuário:</strong>{' '}
                {reviewModal.request.userName ?? reviewModal.request.userId}
                <br />
                <strong>Papel:</strong>{' '}
                {reviewModal.request.roleName ?? reviewModal.request.roleId}
                <br />
                <strong>Justificativa:</strong> {reviewModal.request.reason}
              </p>
              {/* O contrato so tem campo de texto para a recusa: na
                  aprovacao nao ha onde gravar parecer. */}
              {reviewModal.action === 'reject' ? (
                <Textarea
                  label="Motivo da recusa"
                  value={reviewJustification}
                  onChange={(e) => setReviewJustification(e.target.value)}
                  placeholder="Por que o acesso foi recusado..."
                  rows={3}
                />
              ) : (
                <p className="review-summary">
                  A aprovação concede o papel ao usuário imediatamente.
                </p>
              )}
            </>
          )}
          {reviewMutation.isError && (
            <p className="field-error">{(reviewMutation.error as Error).message}</p>
          )}
        </div>
      </Modal>

      <style>{`
        .iam-page { padding: 1.5rem; }
        .filters { margin-bottom: 1rem; max-width: 280px; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; }
        .empty-message { color: #74777f; font-size: 0.875rem; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        .justification-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .review-actions { display: flex; gap: 0.375rem; }
        .reviewed-info { font-size: 0.8125rem; color: #74777f; }
        .modal-form { display: flex; flex-direction: column; gap: 1rem; }
        .modal-actions { display: flex; gap: 0.5rem; }
        .field-error { color: #ba1a1a; font-size: 0.8125rem; margin: 0; }
        .review-summary { font-size: 0.875rem; color: #191c1e; line-height: 1.6; margin: 0; }
      `}</style>
    </div>
  );
}
